import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './DashboardTour.module.scss';
import { DASHBOARD_TOUR_STEPS } from 'src/constants/tourSteps';

interface RectLike {
  top: number;
  left: number;
  width: number;
  height: number;
}

const RECT_PADDING = 8;
const TOOLTIP_OFFSET = 14;

interface DashboardTourProps {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const getElementRect = (targetId: string): RectLike | null => {
  const element = document.getElementById(targetId);

  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();

  return {
    // `getBoundingClientRect` is already viewport-relative; keep fixed overlays in the same coordinate space.
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const DashboardTour: React.FC<DashboardTourProps> = ({
  isOpen,
  onComplete,
  onSkip,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<RectLike | null>(null);

  const steps = useMemo(() => DASHBOARD_TOUR_STEPS, []);
  const currentStep = steps[stepIndex];

  const updateRect = useCallback(() => {
    if (!currentStep) {
      setTargetRect(null);

      return;
    }

    const rect = getElementRect(currentStep.targetId);

    setTargetRect(rect);
  }, [currentStep]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateRect();
    const handleResize = () => updateRect();
    const handleScroll = () => updateRect();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateRect]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateRect();
  }, [isOpen, stepIndex, updateRect]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (!targetRect) {
      return;
    }

    const element = document.getElementById(currentStep.targetId);

    if (element) {
      element.scrollIntoView({ block: 'center', inline: 'center' });
    }
  }, [currentStep?.targetId, isOpen, targetRect]);

  const handleNext = () => {
    if (stepIndex >= steps.length - 1) {
      onComplete();
      setStepIndex(0);

      return;
    }

    setStepIndex((prev) => prev + 1);
  };

  const handleBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleSkip = () => {
    onSkip();
    setStepIndex(0);
  };

  if (!isOpen || !currentStep) {
    return null;
  }

  const rect = targetRect;
  const spotlightStyle = rect
    ? {
        top: rect.top - RECT_PADDING,
        left: rect.left - RECT_PADDING,
        width: rect.width + RECT_PADDING * 2,
        height: rect.height + RECT_PADDING * 2,
      }
    : null;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const tooltipTop = rect ? rect.top + rect.height + TOOLTIP_OFFSET : 120;
  const tooltipLeft = rect ? rect.left : 24;
  const tooltipStyle = {
    top: clamp(tooltipTop, 16, viewportHeight - 220),
    left: clamp(tooltipLeft, 16, viewportWidth - 360),
  };

  return (
    <>
      <div className={styles.overlay} />
      {spotlightStyle && (
        <div className={styles.spotlight} style={spotlightStyle} />
      )}
      <div className={styles.tooltip} style={tooltipStyle}>
        <div className={styles.card}>
          <div className={styles.title}>{t(currentStep.titleKey)}</div>
          <div className={styles.description}>
            {t(currentStep.descriptionKey)}
          </div>
          <div className={styles.actions}>
            <div className={styles.stepCount}>
              {t('dashboard_tour_step_count', {
                current: stepIndex + 1,
                total: steps.length,
              })}
            </div>
            <div className={styles.buttons}>
              <Button variant="text" size="small" onClick={handleSkip}>
                {t('dashboard_tour_skip')}
              </Button>
              {stepIndex > 0 && (
                <Button variant="outlined" size="small" onClick={handleBack}>
                  {t('dashboard_tour_back')}
                </Button>
              )}
              <Button variant="contained" size="small" onClick={handleNext}>
                {stepIndex >= steps.length - 1
                  ? t('dashboard_tour_finish')
                  : t('dashboard_tour_next')}
              </Button>
            </div>
          </div>
          <div className={styles.mobileHint}>
            {t('dashboard_tour_mobile_hint')}
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardTour;
